import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui'
import type { CapabilityDescriptor } from '@/types'

export interface ModelCapabilitySummaryProps {
  capability: CapabilityDescriptor
  className?: string
}

function fmtTokens(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  if (n >= 1000) return `${Math.round(n / 1000)}k`
  return String(n)
}

/**
 * Renders a model's real capability fields, shaped per `capability.modality`.
 * Reads the catalog capability descriptor directly — preserves the
 * multi-modal detail that the legacy flattening adapter dropped.
 */
export function ModelCapabilitySummary({ capability, className }: ModelCapabilitySummaryProps) {
  const wrap = cn('flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground', className)

  switch (capability.modality) {
    case 'text':
      return (
        <div className={wrap}>
          <Badge variant="outline">ctx {fmtTokens(capability.max_input_tokens)}</Badge>
          {capability.max_output_tokens != null && (
            <Badge variant="outline">out {fmtTokens(capability.max_output_tokens)}</Badge>
          )}
          {capability.supports_vision && <Badge variant="info">vision</Badge>}
          {capability.supports_reasoning && <Badge variant="warning">reasoning</Badge>}
          {capability.supports_function_calling && <Badge variant="secondary">tools</Badge>}
          {capability.supports_json_mode && <Badge variant="secondary">json</Badge>}
        </div>
      )

    case 'image':
      return (
        <div className={wrap}>
          {capability.operations?.map((op) => (
            <Badge key={op} variant="secondary">{op}</Badge>
          ))}
          {capability.quality_options?.length > 0 && (
            <Badge variant="outline">quality: {capability.quality_options.join('/')}</Badge>
          )}
          {capability.output_formats?.length > 0 && (
            <Badge variant="outline">{capability.output_formats.join('/')}</Badge>
          )}
          <Badge variant="outline">max {capability.max_images_out}</Badge>
          {capability.supports_transparency && <Badge variant="info">transparency</Badge>}
        </div>
      )

    case 'tts':
      return (
        <div className={wrap}>
          <Badge variant="outline">{capability.voices?.length ?? 0} voices</Badge>
          {capability.audio_formats?.length > 0 && (
            <Badge variant="outline">{capability.audio_formats.join('/')}</Badge>
          )}
          {capability.supports_streaming && <Badge variant="success">streaming</Badge>}
        </div>
      )

    case 'stt':
      return (
        <div className={wrap}>
          <Badge variant="outline">{capability.languages?.length ?? 0} langs</Badge>
          {capability.input_formats?.length > 0 && (
            <Badge variant="outline">{capability.input_formats.join('/')}</Badge>
          )}
          {capability.supports_streaming && <Badge variant="success">streaming</Badge>}
        </div>
      )

    case 'embedding':
      return (
        <div className={wrap}>
          {capability.dimensions != null && <Badge variant="outline">{capability.dimensions} dims</Badge>}
          {capability.max_input_tokens != null && (
            <Badge variant="outline">ctx {fmtTokens(capability.max_input_tokens)}</Badge>
          )}
        </div>
      )

    case 'rerank':
      return (
        <div className={wrap}>
          {capability.max_documents != null && (
            <Badge variant="outline">max {capability.max_documents} docs</Badge>
          )}
        </div>
      )

    default: {
      // Forward-compatible: unrecognized modality (e.g. realtime, #957).
      const unknown = capability as { modality?: string }
      return (
        <div className={wrap}>
          <Badge variant="outline">{unknown.modality ?? 'unknown'}</Badge>
        </div>
      )
    }
  }
}
